import React from 'react';
import { motion } from 'framer-motion';
import { Layers, EyeOff, Repeat, BookOpen, Gift, Megaphone, CheckCircle, ArrowRight } from 'lucide-react';
// Custom SVG Icons for Bonuses
const StopwatchIcon = () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-209">
    <circle cx="12" cy="13" r="8" data-id="element-210"></circle>
    <path d="M12 9v4l2 2" data-id="element-211"></path>
    <path d="M5 3L2 6" data-id="element-212"></path>
    <path d="M22 6L19 3" data-id="element-213"></path>
    <path d="M12 2v3" data-id="element-214"></path>
  </svg>;
const SpeechBubbleIcon = () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9973A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-215">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" data-id="element-216"></path>
    <path d="M9 10l2 2 4-4" data-id="element-217"></path>
  </svg>;
const CheckIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="#1B5E3B" data-id="element-218">
    <circle cx="12" cy="12" r="12" fill="#1B5E3B" data-id="element-219" />
    <path d="M17.25 8.25L10.5 15.75L6.75 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-220" />
  </svg>;
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: 'easeOut'
    }
  })
};
const features = [{
  icon: <Layers size={28} data-id="element-221" />,
  title: 'Creas una vez, cobras para siempre',
  text: 'El manual te muestra exactamente qué crear y por qué las madres lo compran. No adivinas. No desperdicias semanas. Llegas directo a lo que vende.'
}, {
  icon: <EyeOff size={28} data-id="element-222" />,
  title: 'Nadie tiene que saber',
  text: 'Tu jefe, tu familia, tus amigas — nadie sabrá que tienes un negocio hasta que tú decidas contarlo. Privacidad total desde el primer día.'
}, {
  icon: <Repeat size={28} data-id="element-223" />,
  title: 'El sistema trabaja cuando tú no puedes',
  text: 'Una vez configurado, vende mientras llevas a tus hijos al parque, mientras cocinas, mientras duermes. Eso es lo que significa tener un activo, no un trabajo.'
}];
const modules = [{
  icon: <BookOpen size={32} data-id="element-224" />,
  tag: 'GUIA MAESTRA',
  title: 'Imperio de Digital Workbooks',
  text: 'El sistema completo, paso a paso, desde cero hasta tu primera venta. Sin rodeos. Sin relleno. Solo lo que necesitas para empezar esta semana.',
  value: '$97',
  items: ['Cómo identificar exactamente qué productos compran las madres HOY', 'El proceso de creación completo sin experiencia previa', 'Cómo configurar tu tienda para que venda sola, 24/7'],
  highlight: false
}];
export function SolutionSection() {
  return <>
      {/* The Mechanism */}
      <section style={{
      background: 'linear-gradient(180deg, #FAFAF6 0%, #F2EDE4 100%)',
      padding: '80px 24px'
    }} data-id="element-225">
        <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }} data-id="element-226">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} style={{
          textAlign: 'center',
          marginBottom: '56px'
        }} data-id="element-227">
            <span style={{
            display: 'inline-block',
            background: '#EAF4EE',
            color: '#1B5E3B',
            fontFamily: "'Lato', sans-serif",
            fontWeight: 700,
            fontSize: '12px',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            padding: '7px 20px',
            borderRadius: '100px',
            marginBottom: '20px',
            border: '1px solid rgba(27,94,59,0.2)'
          }} data-id="element-228">
              La Salida
            </span>
            <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(26px, 3.5vw, 44px)',
            fontWeight: 900,
            color: '#1C1C1E',
            maxWidth: '800px',
            margin: '0 auto 20px',
            lineHeight: 1.25
          }} data-id="element-229">
              Imagina que mañana por la mañana,{' '}
              <em style={{
              color: '#1B5E3B'
            }} data-id="element-230">
                en lugar de irte a trabajar,
              </em>{' '}
              despiertas y ya tienes ventas esperándote.
            </h2>
            <p style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '17px',
            color: '#4A4A4A',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.8
          }} data-id="element-231">
              Eso no es un sueño. Es lo que pasa cuando tienes un producto
              digital funcionando. Creado una vez. Vendido miles de veces. Sin
              que tú tengas que estar presente.
            </p>
          </motion.div>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '20px'
        }} data-id="element-232">
            {features.map((feature, i) => <motion.div key={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '32px 24px',
            boxShadow: '0 2px 20px rgba(28,28,30,0.06)',
            textAlign: 'center',
            border: '1px solid #E8DDD0'
          }} data-id="element-233">
                <div style={{
              width: '64px',
              height: '64px',
              background: '#EAF4EE',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1B5E3B',
              margin: '0 auto 18px'
            }} data-id="element-234">
                  {feature.icon}
                </div>
                <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '20px',
              fontWeight: 700,
              color: '#1C1C1E',
              marginBottom: '10px'
            }} data-id="element-235">
                  {feature.title}
                </h3>
                <p style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: '14px',
              color: '#4A4A4A',
              lineHeight: 1.75,
              margin: 0
            }} data-id="element-236">
                  {feature.text}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section style={{
      background: 'linear-gradient(135deg, #1C1C1E 0%, #2C2C2C 100%)',
      padding: '80px 24px'
    }} data-id="element-237">
        <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }} data-id="element-238">
          {/* Section header */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} style={{
          textAlign: 'center',
          marginBottom: '52px'
        }} data-id="element-239">
            <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(26px, 3.5vw, 42px)',
            fontWeight: 700,
            color: '#C9973A',
            marginBottom: '10px'
          }} data-id="element-240">
              Todo lo que recibes hoy
            </h2>
            <p style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '16px',
            color: '#B0B0B0',
            maxWidth: '500px',
            margin: '0 auto',
            lineHeight: 1.7
          }} data-id="element-241">
              El sistema exacto, paso a paso, con todo lo que necesitas para
              crear tu primer PDF y empezar a venderlo esta misma semana. Todo
              incluido en tu compra de $6.36.
            </p>
          </motion.div>

          {/* Guía Maestra card — horizontal with image */}
          <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} style={{
          background: '#fff',
          borderRadius: '20px',
          overflow: 'hidden',
          marginBottom: '60px',
          border: '2px solid #C9973A',
          boxShadow: '0 8px 40px rgba(201,151,58,0.15)'
        }} data-id="element-242">
            {/* Top badge */}
            <div style={{
            background: 'linear-gradient(90deg, #1B5E3B, #2D7A52)',
            padding: '10px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }} data-id="element-243">
              <span style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: '11px',
              fontWeight: 900,
              letterSpacing: '2px',
              color: '#fff',
              textTransform: 'uppercase'
            }} data-id="element-244">
                Guía Maestra
              </span>
              <span style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: '12px',
              color: 'rgba(255,255,255,0.6)',
              textDecoration: 'line-through'
            }} data-id="element-245">
                Valor: $97
              </span>
            </div>
            <div style={{
            display: 'flex',
            flexWrap: 'wrap'
          }} data-id="element-246">
              <div style={{
              flex: '1 1 300px',
              minHeight: '280px'
            }} data-id="element-247">
                <img src="https://cdn.magicpatterns.com/uploads/8Nv58TQVJ37zLdVQx57AUq/heylloyd-image-1771891824158.jpg" alt="Imperio de Digital Workbooks" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center 15%'
              }} data-id="element-248" />
              </div>
              <div style={{
              flex: '1 1 400px',
              padding: '32px'
            }} data-id="element-249">
                <h4 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '24px',
                fontWeight: 700,
                color: '#1C1C1E',
                marginBottom: '12px',
                lineHeight: 1.2
              }} data-id="element-250">
                  Imperio de Digital Workbooks
                </h4>
                <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '14px',
                fontStyle: 'italic',
                color: '#555',
                marginBottom: '16px',
                borderLeft: '3px solid #1B5E3B',
                paddingLeft: '12px'
              }} data-id="element-251">
                  "El sistema completo para crear tu primer producto digital y
                  venderlo desde esta semana."
                </p>
                <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '14px',
                color: '#4A4A4A',
                lineHeight: 1.6,
                marginBottom: '20px'
              }} data-id="element-252">
                  Todo lo que necesitas, paso a paso, desde cero hasta tu
                  primera venta. Sin rodeos, sin relleno, sin experiencia
                  previa.
                </p>
                <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }} data-id="element-253">
                  {['Cómo identificar exactamente qué productos compran las madres HOY', 'El proceso de creación completo sin experiencia previa', 'Cómo configurar tu tienda para que venda sola, 24/7'].map((item, i) => <li key={i} style={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: '10px',
                  marginBottom: '8px',
                  fontSize: '13px',
                  color: '#333'
                }} data-id="element-254">
                      <div style={{
                    marginTop: '3px',
                    flexShrink: 0
                  }} data-id="element-255">
                        <CheckIcon data-id="element-256" />
                      </div>
                      <span data-id="element-257">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Bonus showcase title */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} style={{
          textAlign: 'center',
          marginBottom: '20px'
        }} data-id="element-258">
            <span style={{
            display: 'inline-block',
            background: 'rgba(201,151,58,0.15)',
            border: '1px solid rgba(201,151,58,0.4)',
            color: '#C9973A',
            fontFamily: "'Lato', sans-serif",
            fontWeight: 900,
            fontSize: '11px',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            padding: '7px 20px',
            borderRadius: '100px',
            marginBottom: '18px'
          }} data-id="element-259">
              Incluido en tu compra
            </span>

            {/* Decorative line */}
            <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '18px'
          }} data-id="element-260">
              <div style={{
              height: '1px',
              width: '60px',
              background: 'linear-gradient(90deg, transparent, rgba(201,151,58,0.5))'
            }} data-id="element-261" />
              <Gift size={18} style={{
              color: '#C9973A'
            }} data-id="element-262" />
              <div style={{
              height: '1px',
              width: '60px',
              background: 'linear-gradient(90deg, rgba(201,151,58,0.5), transparent)'
            }} data-id="element-263" />
            </div>

            <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(26px, 3.5vw, 38px)',
            fontWeight: 900,
            color: '#FAFAF6',
            margin: '0 auto',
            lineHeight: 1.2,
            maxWidth: '640px',
            letterSpacing: '-0.3px'
          }} data-id="element-264">
              Más{' '}
              <span style={{
              position: 'relative',
              display: 'inline-block',
              color: '#C9973A'
            }} data-id="element-265">
                2 bonos
                <span style={{
                position: 'absolute',
                bottom: '-3px',
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, #C9973A, rgba(201,151,58,0.3))',
                borderRadius: '2px'
              }} data-id="element-266" />
              </span>{' '}
              que eliminan los obstáculos{' '}
              <em style={{
              color: '#E8DDD0',
              fontStyle: 'italic'
            }} data-id="element-267">
                más comunes
              </em>
            </h3>
          </motion.div>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          marginBottom: '60px'
        }} data-id="element-268">
            {modules.map((mod, i) => {})}
          </div>

          {/* NEW BONUS SHOWCASE SECTION */}
          <div style={{
          marginBottom: '60px'
        }} data-id="element-269">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} style={{
            marginBottom: '40px'
          }} data-id="element-270">
              {/* Bonus 1 Card */}
              <div style={{
              background: '#fff',
              borderRadius: '20px',
              overflow: 'hidden',
              marginBottom: '30px',
              display: 'flex',
              flexDirection: 'column',
              md: {
                flexDirection: 'row'
              }
            }} data-id="element-271">
                <div style={{
                display: 'flex',
                flexWrap: 'wrap'
              }} data-id="element-272">
                  <div style={{
                  flex: '1 1 300px',
                  minHeight: '300px'
                }} data-id="element-273">
                    <img src="https://cdn.magicpatterns.com/uploads/e6vFJezZeCRtiKNMhRFAGX/heylloyd-image-1771953847604.png" alt="Plan Anti-Parálisis 48h" style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }} data-id="element-274" />
                  </div>
                  <div style={{
                  flex: '1 1 400px',
                  padding: '32px'
                }} data-id="element-275">
                    <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }} data-id="element-276">
                      <span style={{
                      background: '#EAF4EE',
                      color: '#1B5E3B',
                      padding: '4px 12px',
                      borderRadius: '100px',
                      fontSize: '11px',
                      fontWeight: 900,
                      letterSpacing: '1px'
                    }} data-id="element-277">
                        BONO GRATIS #1
                      </span>
                      <span style={{
                      color: '#8A8A8A',
                      fontSize: '12px',
                      textDecoration: 'line-through'
                    }} data-id="element-278">
                        Valor: $47
                      </span>
                    </div>
                    <h4 style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#1C1C1E',
                    marginBottom: '8px',
                    lineHeight: 1.2
                  }} data-id="element-279">
                      Plan Anti‑Parálisis 48h: Tu Primer PDF Listo para Vender
                      (sin adivinar)
                    </h4>
                    <p style={{
                    fontFamily: "'Lato', sans-serif",
                    fontSize: '14px',
                    fontStyle: 'italic',
                    color: '#555',
                    marginBottom: '16px',
                    borderLeft: '3px solid #1B5E3B',
                    paddingLeft: '12px'
                  }} data-id="element-280">
                      "El miedo más grande no es crear el PDF. Es comprarlo,
                      abrirlo… y no saber por dónde empezar."
                    </p>
                    <p style={{
                    fontFamily: "'Lato', sans-serif",
                    fontSize: '14px',
                    color: '#4A4A4A',
                    lineHeight: 1.6,
                    marginBottom: '20px'
                  }} data-id="element-281">
                      Un plan de 48 horas dividido en bloques de 30 a 45 minutos
                      para mamás con poco tiempo y muchas ganas. No teoría. No
                      videos largos. Un orden exacto que te lleva de "no sé qué
                      hacer" a "ya lo tengo listo para publicar".
                    </p>
                    <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }} data-id="element-282">
                      {['Ruta de ejecución 48h dividida en bloques cortos', 'Estructura ganadora de 7 secciones lista para copiar', 'Checklist "Mínimo Vendible" para saber cuándo tu PDF está listo', 'Semáforo de calidad Verde/Amarillo/Rojo para publicar sin dudas', 'Plan anti-bloqueo para cuando te atoras en diseño, contenido o tiempo', 'Fórmula de nombre para que tu workbook atraiga a la clienta correcta'].map((item, i) => <li key={i} style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '10px',
                      marginBottom: '8px',
                      fontSize: '13px',
                      color: '#333'
                    }} data-id="element-283">
                          <div style={{
                        marginTop: '3px',
                        flexShrink: 0
                      }} data-id="element-284">
                            <CheckIcon data-id="element-285" />
                          </div>
                          <span data-id="element-286">{item}</span>
                        </li>)}
                    </ul>
                    <p style={{
                    marginTop: '20px',
                    fontSize: '13px',
                    fontStyle: 'italic',
                    color: '#1B5E3B',
                    fontWeight: 700
                  }} data-id="element-287">
                      "El primer PDF no tiene que ser tu obra maestra. Tiene que
                      ser tu primer ingreso."
                    </p>
                  </div>
                </div>
              </div>

              {/* Bonus 2 Card */}
              <div style={{
              background: '#fff',
              borderRadius: '20px',
              overflow: 'hidden',
              marginBottom: '30px',
              display: 'flex',
              flexDirection: 'column'
            }} data-id="element-288">
                <div style={{
                display: 'flex',
                flexWrap: 'wrap'
              }} data-id="element-289">
                  <div style={{
                  flex: '1 1 300px',
                  minHeight: '300px'
                }} data-id="element-290">
                    <img src="https://cdn.magicpatterns.com/uploads/qcXnwGrYg1YfZtpG4xt1kX/heylloyd-image-1771953770531.png" alt="Cierra-Ventas Express" style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }} data-id="element-291" />
                  </div>
                  <div style={{
                  flex: '1 1 400px',
                  padding: '32px'
                }} data-id="element-292">
                    <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }} data-id="element-293">
                      <span style={{
                      background: '#FDF3DC',
                      color: '#C9973A',
                      padding: '4px 12px',
                      borderRadius: '100px',
                      fontSize: '11px',
                      fontWeight: 900,
                      letterSpacing: '1px'
                    }} data-id="element-294">
                        BONO GRATIS #2
                      </span>
                      <span style={{
                      color: '#8A8A8A',
                      fontSize: '12px',
                      textDecoration: 'line-through'
                    }} data-id="element-295">
                        Valor: $47
                      </span>
                    </div>
                    <h4 style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#1C1C1E',
                    marginBottom: '8px',
                    lineHeight: 1.2
                  }} data-id="element-296">
                      Cierra‑Ventas Express: 25 Respuestas que Cierran Ventas
                      (sin sonar intensa)
                    </h4>
                    <p style={{
                    fontFamily: "'Lato', sans-serif",
                    fontSize: '14px',
                    fontStyle: 'italic',
                    color: '#555',
                    marginBottom: '16px',
                    borderLeft: '3px solid #C9973A',
                    paddingLeft: '12px'
                  }} data-id="element-297">
                      "Puedes tener el mejor PDF del mundo. Pero si no sabes qué
                      responder cuando te preguntan… pierdes la venta."
                    </p>
                    <p style={{
                    fontFamily: "'Lato', sans-serif",
                    fontSize: '14px',
                    color: '#4A4A4A',
                    lineHeight: 1.6,
                    marginBottom: '20px'
                  }} data-id="element-298">
                      Las 25 respuestas exactas para convertir comentarios y
                      mensajes directos en pagos reales, sin rogar, sin
                      improvisar, sin sonar desesperada. Abres el archivo,
                      copias la respuesta y la pegas. Listo.
                    </p>
                    <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }} data-id="element-299">
                      {['10 respuestas para comentarios públicos (precio, muestra, edad, impresión)', '10 scripts de DM de cero a pago (calificación + recomendación + cierre)', '5 seguimientos suaves para cuando dicen "lo pienso"', '10 cierres sin descuento para cerrar sin bajar tu precio', 'La fórmula 3×1 para recomendar siempre la opción correcta', 'Mini FAQ con las 5 preguntas más repetidas ya respondidas', 'Cheat Sheet imprimible con las respuestas más poderosas en 1 sola hoja'].map((item, i) => <li key={i} style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '10px',
                      marginBottom: '8px',
                      fontSize: '13px',
                      color: '#333'
                    }} data-id="element-300">
                          <div style={{
                        marginTop: '3px',
                        flexShrink: 0
                      }} data-id="element-301">
                            <CheckIcon data-id="element-302" />
                          </div>
                          <span data-id="element-303">{item}</span>
                        </li>)}
                    </ul>
                    <p style={{
                    marginTop: '20px',
                    fontSize: '13px',
                    fontStyle: 'italic',
                    color: '#C9973A',
                    fontWeight: 700
                  }} data-id="element-304">
                      "No necesitas saber vender. Solo necesitas saber qué
                      decir."
                    </p>
                  </div>
                </div>
              </div>

              {/* Closing Copy Block */}
              <div style={{
              background: '#151515',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center',
              border: '1px solid #333'
            }} data-id="element-305">
                <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '16px',
                color: '#E8DDD0',
                marginBottom: '20px',
                lineHeight: 1.6
              }} data-id="element-306">
                  Estos 2 bonos son la razon por lo que la gente compra un curso
                  y no ejecuta:
                </p>
                <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '24px'
              }} data-id="element-307">
                  <p style={{
                  margin: 0,
                  color: '#FAFAF6',
                  fontSize: '15px'
                }} data-id="element-308">
                    <span style={{
                    color: '#C9973A',
                    fontWeight: 900
                  }} data-id="element-309">
                      #1
                    </span>{' '}
                    No saben por dónde empezar →{' '}
                    <strong data-id="element-310">Plan Anti‑Parálisis lo resuelve.</strong>
                  </p>
                  <p style={{
                  margin: 0,
                  color: '#FAFAF6',
                  fontSize: '15px'
                }} data-id="element-311">
                    <span style={{
                    color: '#C9973A',
                    fontWeight: 900
                  }} data-id="element-312">
                      #2
                    </span>{' '}
                    No saben cómo vender cuando ya tienen el producto →{' '}
                    <strong data-id="element-313">Cierra‑Ventas Express lo resuelve.</strong>
                  </p>
                </div>
                <p style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '18px',
                color: '#fff',
                margin: 0
              }} data-id="element-314">
                  Juntos valen{' '}
                  <span style={{
                  textDecoration: 'line-through',
                  color: '#888'
                }} data-id="element-315">
                    $94
                  </span>
                  .{' '}
                  <span style={{
                  color: '#C9973A',
                  fontWeight: 700
                }} data-id="element-316">
                    Hoy son tuyos sin costo adicional cuando te unes hoy.
                  </span>
                </p>
              </div>
            </motion.div>
          </div>

          {/* Value stack */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} style={{
          background: 'rgba(201,151,58,0.08)',
          border: '1px solid rgba(201,151,58,0.3)',
          borderRadius: '18px',
          padding: '28px 36px'
        }} data-id="element-317">
            <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            alignItems: 'center'
          }} data-id="element-318">
              <div data-id="element-319">
                {['Guía Maestra: $97', 'Bono 1 — Plan Anti-Parálisis 48h: $47', 'Bono 2 — Cierra-Ventas Express: $47'].map((line, i) => <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                marginBottom: '9px',
                fontFamily: "'Lato', sans-serif",
                fontSize: '14px',
                color: '#E8DDD0'
              }} data-id="element-320">
                    <CheckCircle size={14} style={{
                  color: '#C9973A',
                  flexShrink: 0
                }} data-id="element-321" />
                    {line}
                  </div>)}
              </div>
              <div style={{
              textAlign: 'center'
            }} data-id="element-322">
                <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '13px',
                color: '#6A6A6A',
                marginBottom: '2px'
              }} data-id="element-323">
                  Valor total:{' '}
                  <span style={{
                  textDecoration: 'line-through'
                }} data-id="element-324">
                    $191
                  </span>
                </p>
                <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '14px',
                color: '#E8DDD0',
                marginBottom: '2px'
              }} data-id="element-325">
                  Precio especial HOY:
                </p>
                <p style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '60px',
                fontWeight: 900,
                color: '#C9973A',
                lineHeight: 1,
                margin: '0 0 20px'
              }} data-id="element-326">
                  $6.36
                </p>
                <a href="#precio" style={{
                textDecoration: 'none',
                display: 'block'
              }} data-id="element-327">
                  <button className="cta-pulse" style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #1B5E3B, #2D7A52)',
                  color: '#fff',
                  fontFamily: "'Lato', sans-serif",
                  fontWeight: 900,
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: '2px solid #C9973A',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }} data-id="element-328">
                    OBTENER AHORA
                    <ArrowRight size={15} data-id="element-329" />
                  </button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}